import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { User, Mail, Building2, Shield, Calendar, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const ROLES = {
  ADMIN_ESTADUAL: { label: 'Admin Estadual', color: 'bg-blue-100 text-blue-800' },
  CASA_CIVIL: { label: 'Casa Civil', color: 'bg-blue-100 text-blue-800' },
  CGE: { label: 'CGE', color: 'bg-green-100 text-green-800' },
  SECRETARIA_EXECUTORA: { label: 'Secretaria Executora', color: 'bg-yellow-100 text-yellow-800' },
  UNIDADE_INTERNA: { label: 'Unidade Interna', color: 'bg-orange-100 text-orange-800' },
  JURIDICO: { label: 'Jurídico', color: 'bg-cyan-100 text-cyan-800' },
  GOVERNADOR: { label: 'Governador', color: 'bg-indigo-100 text-blue-800' },
  OBSERVADOR_EXTERNO: { label: 'Observador Externo', color: 'bg-slate-100 text-slate-800' },
};

const PERMISSIONS = {
  create: 'Criar atos',
  edit: 'Editar atos',
  distribute: 'Distribuir atos',
  validate: 'Validar evidências',
  upload_evidence: 'Enviar evidências',
  approve: 'Aprovar respostas',
  respond: 'Responder atos',
  close: 'Encerrar atos',
  view: 'Visualizar',
  audit: 'Acessar auditoria',
  manage_users: 'Gerenciar usuários'
};

const ROLE_PERMISSIONS = {
  ADMIN_ESTADUAL: ["create", "edit", "distribute", "validate", "upload_evidence", "approve", "respond", "close", "view", "audit", "manage_users"],
  CASA_CIVIL: ["create", "edit", "distribute", "validate", "approve", "respond", "close", "view", "audit"],
  CGE: ["create", "edit", "distribute", "validate", "upload_evidence", "approve", "respond", "view", "audit"],
  SECRETARIA_EXECUTORA: ["edit", "upload_evidence", "respond", "view"],
  UNIDADE_INTERNA: ["upload_evidence", "view"],
  JURIDICO: ["edit", "validate", "respond", "view"],
  GOVERNADOR: ["view"],
  OBSERVADOR_EXTERNO: ["view"]
};

export const SettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userPermissions = ROLE_PERMISSIONS[user?.role] || [];

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Configurações</h1>
          <p className="text-slate-500 mt-1">
            Gerencie sua conta e preferências
          </p>
        </div>

        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle>Perfil</CardTitle>
            <CardDescription>Informações da sua conta</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-6">
              <Avatar className="w-20 h-20">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[#1d4ed8] text-white text-2xl">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Nome</p>
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">E-mail</p>
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Estado</p>
                      <p className="text-sm font-medium text-slate-900">{user?.tenant_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Perfil</p>
                      <Badge className={ROLES[user?.role]?.color}>
                        {ROLES[user?.role]?.label || user?.role}
                      </Badge>
                    </div>
                  </div>
                </div>
                {user?.created_at && (
                  <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Membro desde</p>
                      <p className="text-sm font-medium text-slate-900">
                        {format(new Date(user.created_at), 'dd/MM/yyyy')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Permissions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Permissões</CardTitle>
            <CardDescription>
              Suas permissões baseadas no perfil {ROLES[user?.role]?.label}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(PERMISSIONS).map(([key, label]) => (
                <div
                  key={key}
                  className={`p-3 rounded-lg border ${
                    userPermissions.includes(key)
                      ? 'border-green-200 bg-green-50'
                      : 'border-slate-200 bg-slate-50 opacity-50'
                  }`}
                >
                  <p className={`text-sm font-medium ${
                    userPermissions.includes(key) ? 'text-green-800' : 'text-slate-500'
                  }`}>
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* System Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Sistema</CardTitle>
            <CardDescription>Informações do sistema BlueGov</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Versão</span>
                <span className="text-sm font-mono text-slate-700">1.0.0</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Ambiente</span>
                <span className="text-sm font-mono text-slate-700">Produção</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">ID do Usuário</span>
                <span className="text-sm font-mono text-slate-700">{user?.user_id}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default SettingsPage;
